import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatusBadge, FilterChips } from "@/components/app/PageBits";
import { SwipeTr } from "@/components/app/SwipeTr";
import { fmtINR, fmtDate, CYCLE_LABEL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Wallet, Download, Mail, Search, Percent, Pencil, Ban, AlertTriangle, History, UserCog, MoreHorizontal, Banknote, Smartphone, Landmark } from "lucide-react";
import { downloadInvoicePdf, parseBlobError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

export default function Invoices() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [outstanding, setOutstanding] = useState(searchParams.get("outstanding") === "1");
  const [overdue, setOverdue] = useState(searchParams.get("overdue") === "1");
  const [payOpen, setPayOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ amount: "", method: "bank_transfer", reference_no: "", notes: "", paid_at: new Date().toISOString().slice(0,10) });
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const canEdit = isSuper || (user?.role === "admin" && (user?.admin_role || "editor") !== "viewer");
  const [quickFor, setQuickFor] = useState(null);
  const [quickBusy, setQuickBusy] = useState(false);

  // ---------- super-admin manual corrections ----------
  const [discOpen, setDiscOpen] = useState(false);
  const [discFor, setDiscFor] = useState(null);
  const [disc, setDisc] = useState({ type: "amount", value: "", reason: "" });
  const [discBusy, setDiscBusy] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ amount: "", cycle_start: "", cycle_end: "", due_date: "", notes: "", reason: "" });
  const [editBusy, setEditBusy] = useState(false);

  const [voidOpen, setVoidOpen] = useState(false);
  const [voidFor, setVoidFor] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidBusy, setVoidBusy] = useState(false);

  // ---------- correction history panel ----------
  const [histOpen, setHistOpen] = useState(false);
  const [histFor, setHistFor] = useState(null);
  const [histData, setHistData] = useState(null);
  const [histLoading, setHistLoading] = useState(false);

  const openHistory = async (inv) => {
    setHistFor(inv);
    setHistData(null);
    setHistOpen(true);
    setHistLoading(true);
    try {
      const r = await api.get(`/invoices/${inv.id}/history`);
      setHistData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally {
      setHistLoading(false);
    }
  };

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (q.trim()) params.q = q.trim();
    if (outstanding) params.outstanding = true;
    if (overdue) params.overdue = true;
    const r = await api.get("/invoices", { params });
    setList(r.data);
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q, outstanding, overdue]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (status && status !== "all") next.set("status", status);
    if (q.trim()) next.set("q", q.trim());
    if (overdue) next.set("overdue", "1");
    if (outstanding) next.set("outstanding", "1");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q, overdue, outstanding]);

  const clearFilters = () => { setStatus("all"); setQ(""); setOverdue(false); setOutstanding(false); };
  const filterLabel = overdue ? "Overdue" : outstanding ? "Outstanding" : null;

  const chipValue = overdue ? "overdue" : status;
  const onChip = (v) => {
    setOverdue(v === "overdue");
    setOutstanding(false);
    setStatus(v === "overdue" ? "all" : v);
  };

  const quickPay = async (method) => {
    const inv = quickFor;
    setQuickBusy(true);
    try {
      const r = await api.post("/payments", {
        invoice_id: inv.id, amount: Number(inv.balance), method,
        paid_at: new Date().toISOString().slice(0, 10), receipt_delay_seconds: 10,
      });
      setQuickFor(null); load();
      toast.success(`${inv.invoice_no} marked paid`, {
        description: `${fmtINR(inv.balance)} recorded`,
        duration: 6000,
        action: { label: "Undo", onClick: async () => {
          try { await api.delete(`/payments/${r.data.id}`); toast.success("Payment undone"); load(); }
          catch (e) { toast.error(e?.response?.data?.detail || "Undo failed"); }
        } },
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setQuickBusy(false); }
  };

  const submitPay = async () => {
    try {
      await api.post("/payments", { invoice_id: payFor.id, ...pay, amount: Number(pay.amount) });
      toast.success("Payment recorded"); setPayOpen(false); setPay({amount:"",method:"bank_transfer",reference_no:"",notes:"",paid_at:new Date().toISOString().slice(0,10)}); load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = async (inv) => {
    try { await downloadInvoicePdf(inv); toast.success(`Downloaded ${inv.invoice_no}`); }
    catch(e){ toast.error(await parseBlobError(e)); }
  };
  const sendEmail = async (inv) => {
    try { const r = await api.post(`/invoices/${inv.id}/send-email`); toast.success(`Emailed to ${r.data.to}`); }
    catch(e){ toast.error(e?.response?.data?.detail || "Email failed"); }
  };

  // ----- discount
  const openDiscount = (inv) => {
    setDiscFor(inv);
    setDisc({ type: "amount", value: "", reason: "" });
    setDiscOpen(true);
  };
  const submitDiscount = async () => {
    if (!disc.reason.trim()) return toast.error("Reason is required");
    if (!Number(disc.value) || Number(disc.value) <= 0) return toast.error("Enter a valid value");
    if (disc.type === "percent" && Number(disc.value) > 100) return toast.error("Percent must be ≤ 100");
    setDiscBusy(true);
    try {
      await api.post(`/invoices/${discFor.id}/discount`, {
        type: disc.type, value: Number(disc.value), reason: disc.reason.trim(),
      });
      toast.success("Discount applied");
      setDiscOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Discount failed"); }
    finally { setDiscBusy(false); }
  };

  // ----- edit
  const openEdit = (inv) => {
    setEditFor(inv);
    setEditForm({
      amount: String(inv.amount ?? ""),
      cycle_start: inv.cycle_start || "",
      cycle_end: inv.cycle_end || "",
      due_date: inv.due_date || "",
      notes: inv.notes || "",
      reason: "",
    });
    setEditOpen(true);
  };
  const submitEdit = async () => {
    if (!editForm.reason.trim()) return toast.error("Reason is required");
    const patch = { reason: editForm.reason.trim() };
    if (editForm.amount !== "" && Number(editForm.amount) !== editFor.amount) patch.amount = Number(editForm.amount);
    if (editForm.cycle_start && editForm.cycle_start !== editFor.cycle_start) patch.cycle_start = editForm.cycle_start;
    if (editForm.cycle_end && editForm.cycle_end !== editFor.cycle_end) patch.cycle_end = editForm.cycle_end;
    if (editForm.due_date && editForm.due_date !== (editFor.due_date || "")) patch.due_date = editForm.due_date;
    if ((editForm.notes || "") !== (editFor.notes || "")) patch.notes = editForm.notes;
    if (Object.keys(patch).length === 1) return toast.error("No fields changed");
    setEditBusy(true);
    try {
      await api.patch(`/invoices/${editFor.id}`, patch);
      toast.success("Invoice updated");
      setEditOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Edit failed"); }
    finally { setEditBusy(false); }
  };

  // ----- void
  const openVoid = (inv) => { setVoidFor(inv); setVoidReason(""); setVoidOpen(true); };
  const submitVoid = async () => {
    if (!voidReason.trim()) return toast.error("Reason is required");
    setVoidBusy(true);
    try {
      await api.post(`/invoices/${voidFor.id}/void`, { reason: voidReason.trim() });
      toast.success("Invoice voided");
      setVoidOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Void failed"); }
    finally { setVoidBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title={filterLabel ? `${filterLabel} invoices` : "Invoices"}
        subtitle="Every invoice snapshots the rate and area at generation time, so future rate changes never mutate history."
        actions={
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none"/>
              <Input data-testid="inv-search" placeholder="Search name / plot / email / phone"
                value={q} onChange={(e)=>setQ(e.target.value)}
                className="pl-8 w-72"/>
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="inv-status-filter" className="w-40 max-md:hidden"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
            {(filterLabel || q || status !== "all") && (
              <Button data-testid="inv-clear-filters" variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>
            )}
          </div>
        }
      />
      <FilterChips testId="inv-chips" value={chipValue} onChange={onChip} options={[
        { value: "all", label: "All" }, { value: "pending", label: "Pending" }, { value: "partial", label: "Partial" },
        { value: "overdue", label: "Overdue" }, { value: "paid", label: "Paid" },
      ]}/>
      {canEdit && <p data-testid="swipe-hint-text" className="md:hidden -mt-1 mb-3 text-xs text-muted-foreground">Tip: swipe an unpaid card right to mark it paid.</p>}
      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px] m-cards">
          <thead className="bg-muted/60"><tr className="text-left">
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Invoice #</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Plot</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Customer</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Cycle</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Period</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Amount</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Paid</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Balance</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Status</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {list.map((i) => (
              <SwipeTr key={i.id} data-testid={`inv-row-${i.id}`} className={`border-t border-border hover:bg-muted/40 ${i.status === "void" ? "bg-stone-50 text-stone-500" : ""}`}
                enabled={canEdit && i.status !== "paid" && i.status !== "void" && Number(i.balance) > 0}
                onSwipe={() => setQuickFor(i)}>
                <td className="px-4 py-3 tabular font-medium">
                  {i.invoice_no}
                  {Number(i.discount_total || 0) > 0 && (
                    <span data-testid={`disc-chip-${i.id}`} className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded border border-purple-200 bg-purple-50 text-purple-800 text-[10px] uppercase tracking-wider font-semibold" title={`Discount applied: ${fmtINR(i.discount_total)}`}>
                      −{fmtINR(i.discount_total)}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{i.plot_no}</td>
                <td className="px-4 py-3">{i.customer_name}</td>
                <td className="px-4 py-3 text-muted-foreground">{CYCLE_LABEL[i.billing_cycle]}</td>
                <td className="px-4 py-3 tabular text-xs text-muted-foreground">{fmtDate(i.cycle_start)} → {fmtDate(i.cycle_end)}</td>
                <td className="px-4 py-3 text-right tabular">{fmtINR(i.amount)}</td>
                <td className="px-4 py-3 text-right tabular text-primary">{fmtINR(i.amount_paid)}</td>
                <td className="px-4 py-3 text-right tabular text-destructive">{fmtINR(i.balance)}</td>
                <td className="px-4 py-3"><StatusBadge status={i.status}/></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1 flex-nowrap">
                    <Button data-testid={`pdf-${i.id}`} size="sm" variant="ghost" onClick={()=>downloadPdf(i)} title="Download PDF"><Download className="h-3.5 w-3.5"/></Button>
                    <Button data-testid={`history-${i.id}`} size="sm" variant="ghost" onClick={()=>openHistory(i)} title="Correction history">
                      <History className="h-3.5 w-3.5"/>
                    </Button>
                    {i.status !== "void" && (
                      <Button data-testid={`email-${i.id}`} size="sm" variant="ghost" onClick={()=>sendEmail(i)} title="Email customer"><Mail className="h-3.5 w-3.5"/></Button>
                    )}
                    {i.status !== "paid" && i.status !== "void" && (
                      <Button data-testid={`pay-${i.id}`} size="sm" variant="outline" onClick={()=>{setPayFor(i);setPay({amount:i.balance,method:"bank_transfer",reference_no:"",notes:"",paid_at:new Date().toISOString().slice(0,10)});setPayOpen(true);}}>
                        <Wallet className="h-3 w-3 mr-1"/>Record
                      </Button>
                    )}
                    {isSuper && i.status !== "void" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button data-testid={`more-${i.id}`} size="sm" variant="ghost" title="Super admin corrections">
                            <MoreHorizontal className="h-3.5 w-3.5"/>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {i.balance > 0 && (
                            <DropdownMenuItem data-testid={`discount-${i.id}`} onClick={()=>openDiscount(i)} className="text-purple-800 focus:text-purple-900 focus:bg-purple-50 cursor-pointer">
                              <Percent className="h-3.5 w-3.5 mr-2"/> Apply discount
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`edit-${i.id}`} onClick={()=>openEdit(i)} className="cursor-pointer">
                            <Pencil className="h-3.5 w-3.5 mr-2"/> Edit invoice
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`void-${i.id}`} onClick={()=>openVoid(i)} className="text-red-700 focus:text-red-800 focus:bg-red-50 cursor-pointer">
                            <Ban className="h-3.5 w-3.5 mr-2"/> Void invoice
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </td>
              </SwipeTr>
            ))}
            {list.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">No invoices.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Swipe quick-pay method picker */}
      <Dialog open={!!quickFor} onOpenChange={(v)=>{ if (!v && !quickBusy) setQuickFor(null); }}>
        <DialogContent data-testid="quick-pay-dialog">
          <DialogHeader>
            <DialogTitle>Mark {quickFor?.invoice_no} paid</DialogTitle>
            <p className="text-sm text-muted-foreground">Records <span className="tabular text-foreground font-medium">{fmtINR(quickFor?.balance || 0)}</span> for {quickFor?.customer_name}, dated today. How was it paid?</p>
          </DialogHeader>
          <div className="grid gap-2">
            {[["cash", "Cash", Banknote], ["upi", "UPI", Smartphone], ["bank_transfer", "Bank transfer", Landmark]].map(([m, label, Icon]) => (
              <Button key={m} data-testid={`quick-pay-${m}`} variant="outline" disabled={quickBusy} onClick={()=>quickPay(m)}
                className="h-14 justify-start text-base gap-3">
                <Icon className="h-5 w-5 text-primary"/>{label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Record payment */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record payment · {payFor?.invoice_no}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="text-sm text-muted-foreground">Balance due: <span className="tabular text-foreground font-medium">{fmtINR(payFor?.balance || 0)}</span></div>
            <div><Label>Amount</Label><Input data-testid="pay-amount" type="number" step="0.01" value={pay.amount} onChange={(e)=>setPay({...pay,amount:e.target.value})}/></div>
            <div><Label>Paid on</Label><Input data-testid="pay-date" type="date" value={pay.paid_at} max={new Date().toISOString().slice(0,10)} onChange={(e)=>setPay({...pay,paid_at:e.target.value})}/></div>
            <div>
              <Label>Method</Label>
              <Select value={pay.method} onValueChange={(v)=>setPay({...pay,method:v})}>
                <SelectTrigger data-testid="pay-method"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Reference no.</Label><Input data-testid="pay-ref" value={pay.reference_no} onChange={(e)=>setPay({...pay,reference_no:e.target.value})}/></div>
            <div><Label>Notes</Label><Input data-testid="pay-notes" value={pay.notes} onChange={(e)=>setPay({...pay,notes:e.target.value})}/></div>
            <div className="text-xs text-muted-foreground">Overpayments are added to the customer's credit balance and auto-applied to the next invoice.</div>
          </div>
          <DialogFooter><Button data-testid="pay-submit" onClick={submitPay}>Record payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discount */}
      <Dialog open={discOpen} onOpenChange={(v)=>{ if(!discBusy) setDiscOpen(v); }}>
        <DialogContent data-testid="discount-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-800">
              <Percent className="h-4 w-4"/> Apply discount · {discFor?.invoice_no}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="text-xs text-muted-foreground">
              Gross <span className="tabular text-foreground">{fmtINR(discFor?.amount || 0)}</span> · Paid <span className="tabular text-foreground">{fmtINR(discFor?.amount_paid || 0)}</span> · Balance <span className="tabular text-destructive font-medium">{fmtINR(discFor?.balance || 0)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Discount type</Label>
                <Select value={disc.type} onValueChange={(v)=>setDisc({...disc, type: v, value: ""})}>
                  <SelectTrigger data-testid="disc-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amount">Flat amount (₹)</SelectItem>
                    <SelectItem value="percent">Percent (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{disc.type === "percent" ? "Percent (0–100)" : "Amount (₹)"}</Label>
                <Input data-testid="disc-value" type="number" step="0.01" value={disc.value}
                  onChange={(e)=>setDisc({...disc, value: e.target.value})}/>
              </div>
            </div>
            <div>
              <Label>Reason (required, audited)</Label>
              <Input data-testid="disc-reason" value={disc.reason}
                onChange={(e)=>setDisc({...disc, reason: e.target.value})}
                placeholder="e.g. billing error for June cycle"/>
            </div>
            <div className="text-xs text-muted-foreground">
              Discount reduces the invoice balance (and the gross) immediately. It never refunds past payments — cancel the payment first if you need a refund.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDiscOpen(false)} disabled={discBusy}>Cancel</Button>
            <Button data-testid="disc-submit" className="bg-purple-700 hover:bg-purple-800 text-white"
              onClick={submitDiscount} disabled={discBusy}>
              {discBusy ? "Applying…" : "Apply discount"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={editOpen} onOpenChange={(v)=>{ if(!editBusy) setEditOpen(v); }}>
        <DialogContent data-testid="edit-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4"/> Edit invoice · {editFor?.invoice_no}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="text-xs text-muted-foreground">
              Paid so far: <span className="tabular text-foreground">{fmtINR(editFor?.amount_paid || 0)}</span> — new amount must be at least this.
            </div>
            <div><Label>Amount (₹)</Label><Input data-testid="edit-amount" type="number" step="0.01" value={editForm.amount} onChange={(e)=>setEditForm({...editForm, amount: e.target.value})}/></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Cycle start</Label><Input data-testid="edit-cstart" type="date" value={editForm.cycle_start} onChange={(e)=>setEditForm({...editForm, cycle_start: e.target.value})}/></div>
              <div><Label>Cycle end</Label><Input data-testid="edit-cend" type="date" value={editForm.cycle_end} onChange={(e)=>setEditForm({...editForm, cycle_end: e.target.value})}/></div>
            </div>
            <div><Label>Due date</Label><Input data-testid="edit-due" type="date" value={editForm.due_date} onChange={(e)=>setEditForm({...editForm, due_date: e.target.value})}/></div>
            <div><Label>Notes</Label><Input data-testid="edit-notes" value={editForm.notes} onChange={(e)=>setEditForm({...editForm, notes: e.target.value})}/></div>
            <div><Label>Reason (required, audited)</Label><Input data-testid="edit-reason" value={editForm.reason} onChange={(e)=>setEditForm({...editForm, reason: e.target.value})} placeholder="e.g. wrong area captured at generation"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditOpen(false)} disabled={editBusy}>Cancel</Button>
            <Button data-testid="edit-submit" onClick={submitEdit} disabled={editBusy}>
              {editBusy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void */}
      <Dialog open={voidOpen} onOpenChange={(v)=>{ if(!voidBusy) setVoidOpen(v); }}>
        <DialogContent data-testid="void-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-4 w-4"/> Void invoice · {voidFor?.invoice_no}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-900 text-xs">
              This cancels the invoice. The balance is zeroed out and it is excluded from outstanding & overdue reports. Any credit applied at generation is returned to the customer. Cannot be undone — generate a fresh invoice if needed.
            </div>
            <div className="text-xs text-muted-foreground">
              Amount <span className="tabular text-foreground">{fmtINR(voidFor?.amount || 0)}</span> · Paid <span className="tabular text-foreground">{fmtINR(voidFor?.amount_paid || 0)}</span>
            </div>
            <div><Label>Reason (required, audited)</Label><Input data-testid="void-reason" value={voidReason} onChange={(e)=>setVoidReason(e.target.value)} placeholder="e.g. duplicate invoice created in error"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setVoidOpen(false)} disabled={voidBusy}>Cancel</Button>
            <Button data-testid="void-submit" className="bg-red-700 hover:bg-red-800 text-white" onClick={submitVoid} disabled={voidBusy}>
              {voidBusy ? "Voiding…" : "Void permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Correction history (per-invoice timeline) */}
      <Sheet open={histOpen} onOpenChange={setHistOpen}>
        <SheetContent side="right" className="sm:max-w-xl overflow-y-auto" data-testid="invoice-history-panel">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <History className="h-4 w-4"/> Correction history · {histFor?.invoice_no}
            </SheetTitle>
          </SheetHeader>
          <HistoryTimeline data={histData} loading={histLoading} />
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ---------- correction history timeline ---------- */
function HistoryTimeline({ data, loading }) {
  if (loading) return <div className="py-10 text-center text-muted-foreground text-sm" data-testid="history-loading">Loading history…</div>;
  if (!data) return null;
  const entries = data.entries || [];
  const labelMap = {
    generate_invoice: { label: "Invoice generated", color: "border-emerald-300 bg-emerald-50 text-emerald-900" },
    record_payment:   { label: "Payment recorded",  color: "border-blue-300 bg-blue-50 text-blue-900" },
    discount_invoice: { label: "Discount applied",  color: "border-purple-300 bg-purple-50 text-purple-900" },
    edit_invoice:     { label: "Invoice edited",    color: "border-amber-300 bg-amber-50 text-amber-900" },
    void_invoice:     { label: "Invoice voided",    color: "border-red-300 bg-red-50 text-red-900" },
  };
  const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "";
  const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const describe = (e) => {
    const a = e.after || {}, b = e.before || {}, m = e.meta || {};
    if (e.action === "generate_invoice") {
      return <>Invoice <span className="tabular font-medium">{a.invoice_no || data.invoice.invoice_no}</span> created at <span className="tabular font-medium">{money(a.amount)}</span></>;
    }
    if (e.action === "record_payment") {
      return <>Received <span className="tabular font-medium">{money(a.amount)}</span>{a.method ? ` via ${String(a.method).replace("_", " ")}` : ""}</>;
    }
    if (e.action === "discount_invoice") {
      const typeLabel = m.type === "percent" ? `${m.value}%` : money(m.value);
      const applied = a.applied_discount != null ? money(a.applied_discount) : null;
      return (
        <div className="space-y-1">
          <div>Discount of <span className="font-medium">{typeLabel}</span>{applied ? <> → applied <span className="tabular text-purple-800 font-medium">−{applied}</span></> : null}</div>
          <div className="text-xs text-muted-foreground tabular">
            Amount {money(b.amount)} → {money(a.amount)} · Balance {money(b.balance)} → {money(a.balance)}
          </div>
        </div>
      );
    }
    if (e.action === "edit_invoice") {
      const changes = [];
      for (const k of ["amount", "balance", "cycle_start", "cycle_end", "due_date", "notes", "status"]) {
        if (a[k] !== undefined && String(a[k] ?? "") !== String(b[k] ?? "")) {
          const fv = ["amount", "balance"].includes(k) ? money(a[k]) : String(a[k] ?? "—");
          const ov = ["amount", "balance"].includes(k) ? money(b[k]) : String(b[k] ?? "—");
          changes.push(<div key={k} className="text-xs"><span className="text-muted-foreground">{k.replace("_", " ")}:</span> <span className="tabular line-through text-muted-foreground">{ov}</span> → <span className="tabular font-medium">{fv}</span></div>);
        }
      }
      return <div className="space-y-1">{changes.length ? changes : <div className="text-xs text-muted-foreground">(no field diff recorded)</div>}</div>;
    }
    if (e.action === "void_invoice") {
      const credit = m.credit_returned;
      return <div className="space-y-1">
        <div>Status set to <span className="font-medium text-red-800">VOID</span>, balance zeroed</div>
        {credit > 0 && <div className="text-xs text-muted-foreground">Returned <span className="tabular">{money(credit)}</span> to customer credit balance</div>}
      </div>;
    }
    return <code className="text-xs">{e.action}</code>;
  };

  return (
    <div className="mt-6 space-y-4">
      {/* Current state */}
      <div className="rounded-md border border-border bg-muted/40 p-3 text-xs" data-testid="history-current-state">
        <div className="text-muted-foreground uppercase tracking-wider mb-1 text-[10px]">Current state</div>
        <div className="grid grid-cols-2 gap-y-1">
          <span className="text-muted-foreground">Amount</span><span className="tabular text-right">{money(data.invoice.amount)}</span>
          {Number(data.invoice.discount_total || 0) > 0 && (<>
            <span className="text-muted-foreground">Discount total</span><span className="tabular text-right text-purple-800">−{money(data.invoice.discount_total)}</span>
          </>)}
          <span className="text-muted-foreground">Paid</span><span className="tabular text-right text-primary">{money(data.invoice.amount_paid)}</span>
          <span className="text-muted-foreground">Balance</span><span className="tabular text-right text-destructive font-medium">{money(data.invoice.balance)}</span>
          <span className="text-muted-foreground">Status</span><span className="text-right uppercase tracking-wider text-[10px] font-semibold">{data.invoice.status}</span>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground text-sm" data-testid="history-empty">No correction entries recorded for this invoice.</div>
      ) : (
        <ol className="relative border-l border-border ml-2 space-y-5">
          {entries.map((e) => {
            const meta = labelMap[e.action] || { label: e.action, color: "border-border bg-muted text-foreground" };
            const reason = (e.meta || {}).reason || e.after?.last_discount_reason || e.after?.last_edit_reason || e.after?.void_reason;
            return (
              <li key={e.log_id} className="pl-5 relative" data-testid={`history-entry-${e.log_id}`}>
                <span className={`absolute -left-[7px] top-1 h-3 w-3 rounded-full border-2 ${meta.color.replace("bg-", "ring-").split(" ")[0]} bg-background`}
                      style={{ boxShadow: "inset 0 0 0 2px currentColor" }} />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider font-semibold ${meta.color}`}>
                    {meta.label}
                  </span>
                  <span className="text-xs text-muted-foreground tabular">{fmt(e.timestamp)}</span>
                </div>
                <div className="mt-1 text-sm">{describe(e)}</div>
                {reason && (
                  <div className="mt-1 text-xs text-stone-700 italic">"{reason}"</div>
                )}
                <div className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                  <span>by {e.actor_email}</span>
                  {e.impersonating && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-900 text-[10px] font-medium uppercase tracking-wider"
                          title={e.impersonated_by_email ? `Impersonated by ${e.impersonated_by_email}` : "Impersonated session"}>
                      <UserCog className="h-3 w-3" /> Impersonated
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
